import { cachedFetch } from './cached-api-wrapper';

// XPEN-326: keep already-loaded invoice/receipt images (and other document PDFs)
// in memory so re-opening one — or re-rendering it as a thumbnail — is instant.
// We cache the Blob keyed by a caller-chosen key; getCachedImageUrlByPath then
// hands single-document viewers a cache-owned object URL (created once, revoked
// on eviction) so they need no URL lifecycle of their own, while list callers use
// getCachedInvoiceImageBlob and mint (and revoke) a per-mount URL, so evicting an
// entry never breaks a document still on screen. The first display still fetches
// from storage, so this stays compatible with the signed-URL model (XPEN-1913).

const MAX_TOTAL_BYTES = 40 * 1024 * 1024;

const blobCache = new Map(); // key -> { blob, size, objectUrl? }, insertion order = LRU
const inFlight = new Map(); // key -> Promise<Blob>
let totalBytes = 0;

const touch = (key) => {
  const entry = blobCache.get(key);
  if (entry) {
    blobCache.delete(key);
    blobCache.set(key, entry);
  }
  return entry;
};

const store = (key, blob) => {
  if (blobCache.has(key)) return;
  blobCache.set(key, { blob, size: blob.size, objectUrl: null });
  totalBytes += blob.size;
  // Evict least-recently-used, but never the entry we just added.
  while (totalBytes > MAX_TOTAL_BYTES && blobCache.size > 1) {
    const oldest = blobCache.keys().next().value;
    const evicted = blobCache.get(oldest);
    if (evicted.objectUrl) window.URL.revokeObjectURL(evicted.objectUrl);
    totalBytes -= evicted.size;
    blobCache.delete(oldest);
  }
};

/**
 * Resolve the document at `path` (an XPENS API route that returns a signed
 * storage URL) as a Blob, fetching once and serving from memory afterwards.
 * `cacheKey` identifies the document across callers.
 */
export const getCachedImageBlobByPath = (cacheKey, path, cancelToken = null) => {
  const cached = touch(cacheKey);
  if (cached) return Promise.resolve(cached.blob);

  const pending = inFlight.get(cacheKey);
  if (pending) return pending;

  const request = new Promise((resolve, reject) => {
    cachedFetch(
      path,
      'GET',
      {},
      async (response) => {
        const signedUrl = response?.data?.url;
        if (!signedUrl) {
          reject(new Error('No signed URL for document image'));
          return;
        }
        try {
          // Raw window.fetch: signedUrl is a third-party storage URL, and
          // window.fetch does not reject on a non-2xx status.
          const res = await window.fetch(signedUrl);
          if (!res.ok) throw new Error(`Document image fetch failed (${res.status})`);
          const blob = await res.blob();
          store(cacheKey, blob);
          resolve(blob);
        } catch (err) {
          reject(err);
        }
      },
      (err) => reject(err),
      false,
      cancelToken,
      { cacheType: 'pdf' }
    );
  });

  const tracked = request.finally(() => inFlight.delete(cacheKey));
  inFlight.set(cacheKey, tracked);
  return tracked;
};

// Drop-in for a signed URL: resolves to a cache-owned object URL (created once,
// revoked on eviction) so a single-document viewer renders from cache without
// managing the URL's lifecycle. List callers (thumbnails) or callers that already
// revoke their own buffer should use the *Blob helpers and mint their own URL.
export const getCachedImageUrlByPath = (cacheKey, path, cancelToken) =>
  getCachedImageBlobByPath(cacheKey, path, cancelToken).then((blob) => {
    const entry = touch(cacheKey);
    if (entry) {
      if (!entry.objectUrl) entry.objectUrl = window.URL.createObjectURL(entry.blob);
      return entry.objectUrl;
    }
    return window.URL.createObjectURL(blob); // evicted meanwhile; hand back a fresh URL
  });

const invoicePath = (fileName) => `/invoice/pdf/${encodeURIComponent(fileName)}`;

export const getCachedInvoiceImageBlob = (fileName, cancelToken) =>
  getCachedImageBlobByPath(fileName, invoicePath(fileName), cancelToken);

export const getCachedInvoiceImageUrl = (fileName, cancelToken) =>
  getCachedImageUrlByPath(fileName, invoicePath(fileName), cancelToken);

export const __resetInvoiceImageCache = () => {
  blobCache.forEach((entry) => {
    if (entry.objectUrl) window.URL.revokeObjectURL(entry.objectUrl);
  });
  blobCache.clear();
  inFlight.clear();
  totalBytes = 0;
};
